<template>
  <div class="student-profile-page">
    <v-row justify="center" no-gutters>
      <v-col cols="12" class="primary text-left py-1">
        <v-btn fab color="primary" elevation="0" small @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>

      <v-col cols="12" md="11" lg="10" class="px-3 px-sm-6 pb-10">
        <v-card class="profile-hero mt-3 mb-4" flat>
          <v-card-text class="pa-4 pa-sm-6">
            <div class="hero-layout">
              <v-avatar color="primary" size="118" class="hero-avatar">
                <img v-if="avatar" :src="avatar" alt="" />
                <v-icon v-else dark size="70">mdi-account-circle</v-icon>
              </v-avatar>

              <div class="hero-main">
                <div class="hero-name">
                  {{ fullName || 'Student Profile' }}
                </div>
                <div class="hero-address" v-if="addressLine">
                  <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
                  {{ addressLine }}
                </div>
                <div class="hero-actions mt-4">
                  <v-btn color="primary" :href="`mailto:${email}`" :disabled="!email">
                    <v-icon left>mdi-email</v-icon>
                    Email
                  </v-btn>
                  <v-btn outlined color="primary" :href="`tel:${phone}`" :disabled="!phone">
                    <v-icon left>mdi-phone</v-icon>
                    Call
                  </v-btn>
                  <v-btn outlined color="primary" :to="`/chat/${id}`">
                    <v-icon left>mdi-chat</v-icon>
                    Chat
                  </v-btn>
                  <v-btn text color="primary" :href="licence" target="_blank" :disabled="!licence">
                    <v-icon left>mdi-card-account-details-outline</v-icon>
                    Licence
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-row class="mb-4" dense>
          <v-col cols="12" sm="4">
            <v-card class="stats-card" flat>
              <v-card-text>
                <div class="stats-label">Total bookings</div>
                <div class="stats-value">{{ bookingSummary.totalLabel }}</div>
                <div class="stats-caption">Package limit (bookings available)</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="stats-card" flat>
              <v-card-text>
                <div class="stats-label">Used</div>
                <div class="stats-value">{{ bookingSummary.usedLabel }}</div>
                <div class="stats-caption">Hours consumed by bookings</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card class="stats-card" flat>
              <v-card-text>
                <div class="stats-label">Remaining</div>
                <div class="stats-value">{{ bookingSummary.remainingLabel }}</div>
                <div class="stats-caption">Total - used bookings</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <div class="availability-on-profile">
          <div class="availability-on-profile-inner">
            <h3 class="availability-on-profile-title text-center mb-3">
              Weekly availability
            </h3>
            <p class="availability-on-profile-caption text-center mb-4">
              When this student is generally available for lessons.        
            </p>
            <StudentAvailabilityWeekDisplay
              :days="availabilityDays"
              :loading="availabilityLoading"
              empty-message="This student has not added weekly availability yet."
            />
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import Parse from 'parse'
import { mapActions, mapGetters } from 'vuex'
import StudentAvailabilityWeekDisplay from '@/components/student/StudentAvailabilityWeekDisplay.vue'
import {
  defaultDays,
  mapAvailabilityRowsToDays
} from '@/utils/studentAvailabilityShared'

export default {
  components: { StudentAvailabilityWeekDisplay },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['user', 'studentProfile']),
    fullName () {
      return [this.firstName, this.lastName].filter(Boolean).join(' ')
    },
    addressLine () {
      return [this.addressOne, this.city, this.province, this.postalCode, this.country]
        .filter(Boolean)
        .join(', ')
    },
    bookingSummary () {
      const used = Number.isFinite(this.utilizedHours) ? this.utilizedHours : 0
      const total = Number.isFinite(this.totalBookings) ? this.totalBookings : 0
      const hasLimit = total > 0
      const remaining = Math.max(total - used, 0)
      return {
        totalLabel: hasLimit ? String(total) : 'Unlimited',
        usedLabel: this.formatHours(used),
        remainingLabel: hasLimit ? this.formatHours(remaining) : 'Unlimited'
      }
    }
  },
  data () {
    return {
      firstName: '',
      lastName: '',
      avatar: '',
      phone: '',
      email: '',
      addressOne: '',
      postalCode: '',
      city: '',
      province: '',
      country: '',
      licence: '',
      availabilityDays: defaultDays(),
      availabilityLoading: false,
      totalBookings: 0,
      utilizedHours: 0
    }
  },
  methods: {
    ...mapActions(['getStudentProfile']),
    resolvePackageId (value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (value.id) return value.id
      if (typeof value.get === 'function') {
        return value.get('objectId') || ''
      }
      return ''
    },
    formatHours (value) {
      const num = Number(value || 0)
      if (Number.isInteger(num)) return `${num}`
      return num.toFixed(1)
    },
    async loadBookingStats () {
      if (!this.id) return
      try {
        const studentUser = Parse.User.createWithoutData(this.id)

        const userQuery = new Parse.Query('_User')
        const student = await userQuery.get(this.id)

        const packageField = student.get('packageId')
        const packageId = this.resolvePackageId(packageField)
        if (packageId) {
          const packageData = await new Parse.Query('SchoolPackages').get(packageId)
          this.totalBookings = parseInt(packageData?.get('freeBookings')) || 0
        } else {
          this.totalBookings = 0
        }

        const [appointments] = await Promise.all([
          new Parse.Query('Appointments')
            .equalTo('studentId', studentUser)
            .containedIn('status', ['Finished', 'Approved'])
            .find(),
        ])

        let totalHours = 0
        appointments.forEach((item) => {
          totalHours += parseFloat(item.get('hoursLesson')) || 0
        })
        this.utilizedHours = totalHours
      } catch (err) {
        console.error('loadBookingStats error:', err)
        this.totalBookings = 0
        this.utilizedHours = 0
      }
    },
    async loadStudentAvailability () {
      if (!this.id) return
      this.availabilityLoading = true
      try {
        const studentUser = Parse.User.createWithoutData(this.id)
        const Availability = Parse.Object.extend('StudentAvailability')
        const query = new Parse.Query(Availability)
        query.equalTo('studentId', studentUser)
        query.limit(20)
        const rows = await query.find()
        this.availabilityDays = mapAvailabilityRowsToDays(rows)
      } catch (err) {
        console.error('loadStudentAvailability error:', err)
        this.availabilityDays = defaultDays()
      } finally {
        this.availabilityLoading = false
      }
    }
  },
  async created () {
    console.log(this.id)
    if (this.id) {
      await this.getStudentProfile({
        studentId: this.id
      })
      if (this.studentProfile) {
        this.firstName = this.studentProfile.get('firstName')
        this.lastName = this.studentProfile.get('lastName')
        this.avatar = this.studentProfile.get('profilePicture')
        this.phone = this.studentProfile.get('phone')
        this.email = this.studentProfile.get('customEmail')
        this.addressOne = this.studentProfile.get('addressOne')
        this.postalCode = this.studentProfile.get('postalCode')
        this.city = this.studentProfile.get('city')
        this.province = this.studentProfile.get('province')
        this.country = this.studentProfile.get('country')
        this.licence = this.studentProfile.get('uploadLicence')
      }
      await this.loadStudentAvailability()
      await this.loadBookingStats()
    }
  }
}
</script>

<style scoped>
.student-profile-page {
  min-height: 100%;
}

.profile-hero {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.hero-layout {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.hero-avatar {
  flex: 0 0 auto;
}

.hero-main {
  flex: 1 1 320px;
  min-width: 0;
}

.hero-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.hero-address {
  margin-top: 0.45rem;
  color: #5f6b7a;
  font-size: 0.92rem;
  display: flex;
  align-items: center;
}

.hero-actions {
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.stats-card {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

.stats-label {
  font-size: 0.82rem;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.stats-value {
  font-size: 1.45rem;
  font-weight: 700;
  color: #0f172a;
}

.stats-caption {
  font-size: 0.76rem;
  color: #8a94a6;
  margin-top: 0.25rem;
}

.availability-on-profile {
  width: 100%;
}

.availability-on-profile-inner {
  background: linear-gradient(135deg, #2e7d32 0%, #43a047 40%, #66bb6a 100%);
  border-radius: 12px;
  padding: 1rem 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.availability-on-profile-title {
  color: rgba(255, 255, 255, 0.98);
  font-size: 1.15rem;
  font-weight: 700;
}

.availability-on-profile-caption {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.85rem;
  line-height: 1.4;
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 959px) {
  .hero-name {
    font-size: 1.25rem;
  }

  .availability-on-profile {
    padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px) + 32px);
  }
}
</style>
